import {
  BlockBootstrapSampler,
  compareScenarios,
  createRng,
  HistoricalSequenceSampler,
  IidBootstrapSampler,
  IidMultiAssetSampler,
  NoTaxPolicy,
  type Plan,
  ReorderStressSampler,
  type ResultSet,
  RetireIntoYearSampler,
  type ReturnData,
  runMonteCarlo,
  type Sampler,
  type ScenarioComparison,
  type ScenarioSpec,
  type TaxPolicy,
  THAI_CANONICAL_STRESS_YEARS,
  ThailandTaxPolicy,
} from '@projections/engine';

/**
 * The CLIENT-SIDE simulation client (PLAN.md M7 — "RUN THE ENGINE CLIENT-SIDE for the MVP").
 *
 * Runs `@projections/engine`'s Monte Carlo IN THE BROWSER from a Plan + a sampler choice + a seed,
 * exactly like research/ bundles the engine — instant, OFFLINE, dogfoodable with no backend. The
 * engine is pure TS (zod + d3-array only; the data layer is TYPE-ONLY), so it bundles cleanly into
 * the SPA. Same seed → identical ResultSet (the engine's reproducibility guarantee carries here).
 *
 * This is a THIN, typed facade: it maps the UI's sampler/tax selections onto the engine's sampler
 * and TaxPolicy instances, then delegates to `runMonteCarlo` / `compareRetireIntoYears`. No new
 * simulation logic lives here. Results (incl. every path's Ledger) stay in memory for the dashboard
 * and the ledger drill-down — never serialized away (the ledger-first invariant).
 *
 * Mirrors the API's `SimulateRequest` so the same UI form drives a local run OR a future server run.
 */

export type SamplerKind = 'iid' | 'iidMultiAsset' | 'block' | 'historical';
export type TaxPolicyKind = 'none' | 'th';

export interface SimulationParams {
  readonly sampler: SamplerKind;
  /** Path count (ignored by the historical sampler, which enumerates rolling windows). */
  readonly nPaths: number;
  /** Seed for the PRNG → reproducible run. */
  readonly seed: number;
  readonly taxPolicy: TaxPolicyKind;
  /** Block length for `sampler: 'block'`. */
  readonly blockLength?: number;
  /** Fan-chart band percentiles (decimals in [0,1]); defaults to 5/25/50/75/95. */
  readonly percentiles?: readonly number[];
}

/** Construct the engine `Sampler` for a UI sampler choice. PURE — real, not a stub. */
export function samplerFor(kind: SamplerKind, opts?: { blockLength?: number }): Sampler {
  switch (kind) {
    case 'iid':
      return new IidBootstrapSampler();
    case 'iidMultiAsset':
      return new IidMultiAssetSampler();
    case 'block':
      return new BlockBootstrapSampler({ blockLength: opts?.blockLength ?? 5 });
    case 'historical':
      return new HistoricalSequenceSampler();
    default: {
      const _exhaustive: never = kind;
      throw new Error(`unknown sampler ${String(_exhaustive)}`);
    }
  }
}

/** Construct the engine `TaxPolicy` for a UI tax choice. PURE — real, not a stub. */
export function taxPolicyFor(kind: TaxPolicyKind): TaxPolicy {
  return kind === 'th' ? new ThailandTaxPolicy() : new NoTaxPolicy();
}

/**
 * Run a Monte Carlo client-side. Assembles the sampler / tax policy / seeded rng and delegates to
 * the engine's `runMonteCarlo`. Returns the full `ResultSet` (bands, successProbability,
 * terminalWealth(+summary), and EVERY path's Ledger for drill-down).
 *
 * BUILD-AGENT STUB: build the sampler (`samplerFor`), the tax policy (`taxPolicyFor`), the rng
 * (`createRng(params.seed)`), then `return runMonteCarlo(plan, sampler, params.nPaths, { data, rng,
 * taxPolicy, percentiles })`. The pure helpers above are real; this is a few lines of wiring.
 */
export function runSimulation(plan: Plan, data: ReturnData, params: SimulationParams): ResultSet {
  const sampler = samplerFor(
    params.sampler,
    params.blockLength !== undefined ? { blockLength: params.blockLength } : {},
  );
  const taxPolicy = taxPolicyFor(params.taxPolicy);
  const rng = createRng(params.seed);
  return runMonteCarlo(plan, sampler, params.nPaths, {
    data,
    rng,
    taxPolicy,
    ...(params.percentiles ? { percentiles: params.percentiles } : {}),
  });
}

/**
 * Run the stress-scenario comparison (retire-into-YYYY over the canonical Thai stress years, plus
 * the worst/best-first re-orderings the results screen surfaces). Delegates to the engine's
 * `compareRetireIntoYears` / `compareScenarios`. Returns the `ScenarioComparison` (baseline +
 * named scenarios), each keeping its full `ResultSet` for drill-down.
 *
 * BUILD-AGENT STUB: build a baseline sampler + the seeded rng, then
 * `return compareRetireIntoYears(plan, baselineSampler, { data, rng, taxPolicy }, years?)`. The
 * results screen may also assemble explicit `ReorderStressSampler` scenarios via `compareScenarios`.
 */
export function runStressComparison(
  plan: Plan,
  data: ReturnData,
  params: SimulationParams,
): ScenarioComparison {
  const taxPolicy = taxPolicyFor(params.taxPolicy);
  const rng = createRng(params.seed);
  const options = {
    data,
    rng,
    taxPolicy,
    nPaths: params.nPaths,
    ...(params.percentiles ? { percentiles: params.percentiles } : {}),
  };

  // The baseline is the user's chosen sampler — the same distribution the dashboard headline ran on.
  const baseline: ScenarioSpec = {
    name: 'baseline',
    sampler: samplerFor(
      params.sampler,
      params.blockLength !== undefined ? { blockLength: params.blockLength } : {},
    ),
  };

  // Sequence-of-returns stress: worst-first / best-first re-orderings (deterministic, rng-ignored)…
  const stress: ScenarioSpec[] = [
    { name: 'worst-first', sampler: new ReorderStressSampler({ ordering: 'worstFirst' }) },
    { name: 'best-first', sampler: new ReorderStressSampler({ ordering: 'bestFirst' }) },
  ];

  // …plus retire-into-YYYY for each canonical Thai stress year that has a full horizon of real
  // history ahead of it in `data` (years without enough history are skipped, never truncated).
  const periods = new Set(data.periods ?? []);
  const horizon = plan.horizonYears;
  for (const year of THAI_CANONICAL_STRESS_YEARS) {
    const t0 = (data.periods ?? []).indexOf(String(year));
    if (t0 >= 0 && periods.has(String(year)) && t0 + horizon <= (data.periods ?? []).length) {
      stress.push({ name: `retire into ${year}`, sampler: new RetireIntoYearSampler(year) });
    }
  }

  return compareScenarios(plan, baseline, stress, options);
}
